import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

const ACTIVE_CLASSES = [
  "bg-primary",
  "text-primary-content",
  "font-semibold",
  "shadow-sm",
];

const INACTIVE_CLASSES = [
  "text-base-content/60",
  "font-medium",
  "hover:text-base-content",
  "hover:bg-base-100",
];

/**
 * Header mode scope selector.
 *
 * Default: pill click navigates with Turbo.visit(replace) so the server
 * re-renders the sub-page scoped to the new mode.
 *
 * Frame mode (when `data-mode-pill-group-frame-id-value` is set): pill
 * click updates the URL via history.replaceState and reassigns the
 * named turbo frame's `src`. Used on the public playground page so the
 * playground shell stays in place while the frame contents refetch.
 */
export default class ModePillGroupController extends Controller {
  static targets = ["pill"];
  static values = { frameId: String };

  declare readonly pillTargets: HTMLAnchorElement[];
  declare frameIdValue: string;
  declare hasFrameIdValue: boolean;

  select(event: MouseEvent): void {
    // Honor open-in-new-tab gestures.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();

    const link = event.currentTarget as HTMLAnchorElement;
    const pageUrl = link.href;
    const frameUrl = link.dataset.frameUrl;

    if (this.hasFrameIdValue && frameUrl) {
      const frame = document.getElementById(this.frameIdValue) as
        | (HTMLElement & { src: string })
        | null;
      if (frame) {
        history.replaceState({}, "", pageUrl);
        this.markActive(link);
        frame.src = frameUrl;
        return;
      }
    }

    Turbo.visit(pageUrl, { action: "replace" });
  }

  private markActive(active: HTMLAnchorElement): void {
    for (const pill of this.pillTargets) {
      const isActive = pill === active;
      ACTIVE_CLASSES.forEach((c) => pill.classList.toggle(c, isActive));
      INACTIVE_CLASSES.forEach((c) => pill.classList.toggle(c, !isActive));
    }
  }
}
